import React, { useState, useEffect, useCallback } from 'react';
import { runSwingRebalance, applySwingRebalance, getSwingSettings, updateSwingSettings } from '../../api/client';

const FREQ_OPTIONS = [
  { value: 'weekly',    label: 'Weekly (every Monday)' },
  { value: 'biweekly', label: 'Bi-weekly (every 2nd Monday)' },
  { value: 'monthly',  label: 'Monthly (1st trading day)' },
];

function AddRow({ stock }) {
  // Capital Req. is returned directly by the API as `capital_required`.
  // Fall back to entry_price × suggested_qty if missing (defensive).
  const qty        = stock.suggested_qty ?? 0;
  const capitalReq = stock.capital_required ?? (stock.entry_price && qty ? stock.entry_price * qty : null);
  const score      = stock.composite_score;

  return (
    <tr style={{ borderBottom: '1px solid #1a3a1a' }}>
      <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: 'monospace' }}>{stock.symbol}</td>
      <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{stock.sector || '—'}</td>
      <td style={{ padding: '10px 12px' }}>
        <span style={{ color: '#4CAF50', fontWeight: 600 }}>
          {score != null ? Math.round(score) : '—'}
        </span>
      </td>
      <td style={{ padding: '10px 12px' }}>{qty > 0 ? qty : '—'}</td>
      <td style={{ padding: '10px 12px', color: '#4CAF50' }}>
        {capitalReq ? `₹${Math.round(capitalReq).toLocaleString('en-IN')}` : '—'}
      </td>
    </tr>
  );
}

function RemoveRow({ stock }) {
  // API returns the field as `unrealised_pnl` (was being read as estimated_pnl)
  const pnl      = stock.unrealised_pnl;
  const pnlColor = pnl > 0 ? '#4CAF50' : pnl < 0 ? '#f44' : '#aaa';

  return (
    <tr style={{ borderBottom: '1px solid #3a1a1a' }}>
      <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: 'monospace' }}>{stock.symbol}</td>
      <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{stock.sector || '—'}</td>
      <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{stock.reason || '—'}</td>
      <td style={{ padding: '10px 12px' }}>
        {stock.current_price ? `₹${parseFloat(stock.current_price).toLocaleString('en-IN')}` : '—'}
      </td>
      <td style={{ padding: '10px 12px', color: pnlColor, fontWeight: pnl ? 600 : 400 }}>
        {pnl !== undefined && pnl !== null
          ? `${pnl >= 0 ? '+' : ''}₹${Math.round(pnl).toLocaleString('en-IN')}`
          : '—'}
      </td>
    </tr>
  );
}

function HoldRow({ stock }) {
  const pnl      = stock.unrealised_pnl;
  const pnlColor = pnl > 0 ? '#4CAF50' : pnl < 0 ? '#f44' : '#aaa';
  const score    = stock.composite_score;

  return (
    <tr style={{ borderBottom: '1px solid #222' }}>
      <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: 'monospace' }}>{stock.symbol}</td>
      <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{stock.sector || '—'}</td>
      <td style={{ padding: '10px 12px', color: '#666', fontSize: 12 }}>
        {stock.rank ? `#${stock.rank}` : '—'}
        {score != null && (
          <span style={{ marginLeft: 6, color: '#4CAF50', fontWeight: 600 }}>· {Math.round(score)}</span>
        )}
      </td>
      <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>
        {stock.current_price ? `₹${parseFloat(stock.current_price).toLocaleString('en-IN')}` : '—'}
      </td>
      <td style={{ padding: '10px 12px', color: pnlColor, fontWeight: pnl ? 600 : 400 }}>
        {pnl !== undefined && pnl !== null
          ? `${pnl >= 0 ? '+' : ''}₹${Math.round(pnl).toLocaleString('en-IN')}`
          : '—'}
      </td>
      <td style={{ padding: '10px 12px', color: '#666', fontSize: 12 }}>{stock.reason || 'Meets criteria'}</td>
    </tr>
  );
}

function Section({ title, color, count, children, isEmpty }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setCollapsed(c => !c)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color }}>{title}</span>
        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: color + '22', color }}>{count}</span>
        <span style={{ marginLeft: 'auto', color: '#555', fontSize: 12 }}>{collapsed ? '▶ Show' : '▼ Hide'}</span>
      </button>
      {!collapsed && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', border: `1px solid ${color}33` }}>
          {isEmpty ? (
            <div style={{ padding: '16px 16px', color: '#555', fontSize: 13 }}>None</div>
          ) : children}
        </div>
      )}
    </div>
  );
}

export default function SwingRebalance() {
  const [report, setReport]       = useState(null);
  const [running, setRunning]     = useState(false);
  const [applying, setApplying]   = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [settings, setSettings]   = useState(null);
  const [freq, setFreq]           = useState('monthly');
  const [savingFreq, setSavingFreq] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await getSwingSettings();
      setSettings(res.data);
      setFreq(res.data.rebalance_frequency || 'monthly');
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setReport(null);
    try {
      const res = await runSwingRebalance();
      setReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to run rebalance');
    } finally {
      setRunning(false);
    }
  };

  const handleApply = async () => {
    if (!report) return;
    if (!window.confirm('Apply rebalance? This will create/update positions in your portfolio.')) return;
    setApplying(true);
    setError('');
    try {
      await applySwingRebalance(report);
      setSuccess('Rebalance applied successfully!');
      setReport(null);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to apply rebalance');
    } finally {
      setApplying(false);
    }
  };

  const handleSaveFreq = async () => {
    setSavingFreq(true);
    try {
      await updateSwingSettings({ rebalance_frequency: freq });
      setSuccess('Schedule saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to save schedule'); }
    finally { setSavingFreq(false); }
  };

  const adds    = report?.add    || [];
  const removes = report?.remove || [];
  const holds   = report?.hold   || [];

  return (
    <div>
      {error && <div style={{ color: '#f44', marginBottom: 12, padding: '8px 12px', background: '#2a1010', borderRadius: 6 }}>{error}</div>}
      {success && <div style={{ color: '#4CAF50', marginBottom: 12, padding: '8px 12px', background: '#0a2010', borderRadius: 6 }}>{success}</div>}

      {/* Run controls */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Portfolio Rebalance</div>
            <div style={{ fontSize: 12, color: '#888' }}>Compare current portfolio against latest scan results and suggest changes.</div>
          </div>
          <button
            onClick={handleRun}
            disabled={running}
            style={{
              marginLeft: 'auto', padding: '9px 20px', background: running ? '#555' : '#9C27B0',
              color: '#fff', border: 'none', borderRadius: 6, cursor: running ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13,
            }}
          >
            {running ? '⏳ Running...' : '⚖️ Run Rebalance'}
          </button>
        </div>
      </div>

      {/* Report */}
      {report && (
        <div style={{ marginBottom: 20 }}>
          {/* Summary bar */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Add', value: adds.length, color: '#4CAF50' },
              { label: 'Remove', value: removes.length, color: '#f44336' },
              { label: 'Hold', value: holds.length, color: '#888' },
            ].map(s => (
              <div key={s.label} style={{ padding: '10px 20px', background: '#1a1a1a', border: `1px solid ${s.color}44`, borderRadius: 8, textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{s.label}</div>
              </div>
            ))}

            {/* Capital flow tiles — what's freed (from REMOVE) vs needed (for ADD) */}
            {report.capital_freed > 0 && (
              <div style={{ padding: '10px 20px', background: '#1a1a1a', border: '1px solid #2d6a2d', borderRadius: 8, textAlign: 'center', minWidth: 120 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#4CAF50' }}>
                  ₹{Math.round(report.capital_freed).toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>Capital Freed</div>
              </div>
            )}
            {report.capital_needed > 0 && (
              <div style={{ padding: '10px 20px', background: '#1a1a1a', border: '1px solid #2196F3aa', borderRadius: 8, textAlign: 'center', minWidth: 120 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#2196F3' }}>
                  ₹{Math.round(report.capital_needed).toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>Capital Needed</div>
              </div>
            )}
            {/* Net flow — green if freed > needed (no fresh capital required) */}
            {(report.capital_freed > 0 || report.capital_needed > 0) && (() => {
              const net = (report.capital_freed || 0) - (report.capital_needed || 0);
              const netColor = net >= 0 ? '#4CAF50' : '#FF9800';
              const netLabel = net >= 0 ? 'Net Surplus' : 'Net Shortfall';
              return (
                <div style={{ padding: '10px 20px', background: '#1a1a1a', border: `1px solid ${netColor}66`, borderRadius: 8, textAlign: 'center', minWidth: 120 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: netColor }}>
                    {net >= 0 ? '+' : '-'}₹{Math.round(Math.abs(net)).toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>{netLabel}</div>
                </div>
              );
            })()}

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
              <button
                onClick={handleApply}
                disabled={applying || (adds.length === 0 && removes.length === 0)}
                style={{
                  padding: '10px 24px', background: applying ? '#555' : '#4CAF50',
                  color: '#fff', border: 'none', borderRadius: 6,
                  cursor: applying || (adds.length === 0 && removes.length === 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 14,
                }}
              >
                {applying ? 'Applying...' : '✓ Apply Rebalance'}
              </button>
            </div>
          </div>

          {/* ADD section */}
          <Section title="ADD" color="#4CAF50" count={adds.length} isEmpty={adds.length === 0}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1a3a1a', background: '#0a2010' }}>
                    {['Symbol', 'Sector', 'Score', 'Qty', 'Capital Req.'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#4CAF50', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adds.map((s, i) => <AddRow key={i} stock={s} />)}
                </tbody>
              </table>
            </div>
          </Section>

          {/* REMOVE section */}
          <Section title="REMOVE" color="#f44336" count={removes.length} isEmpty={removes.length === 0}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #3a1a1a', background: '#200a0a' }}>
                    {['Symbol', 'Sector', 'Reason', 'Current ₹', 'Est. P&L'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#f44336', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {removes.map((s, i) => <RemoveRow key={i} stock={s} />)}
                </tbody>
              </table>
            </div>
          </Section>

          {/* HOLD section */}
          <Section title="HOLD" color="#888" count={holds.length} isEmpty={holds.length === 0}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2a2a2a', background: '#141414' }}>
                    {['Symbol', 'Sector', 'Rank · Score', 'Current ₹', 'Unreal. P&L', 'Reason'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {holds.map((s, i) => <HoldRow key={i} stock={s} />)}
                </tbody>
              </table>
            </div>
          </Section>

          {report.generated_at && (
            <div style={{ fontSize: 11, color: '#444', marginTop: 8 }}>
              Generated at: {new Date(report.generated_at).toLocaleString('en-IN')}
            </div>
          )}
        </div>
      )}

      {/* Schedule section */}
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Rebalance Schedule</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Frequency</label>
            <select
              value={freq}
              onChange={e => setFreq(e.target.value)}
              style={{ padding: '8px 12px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, minWidth: 220 }}
            >
              {FREQ_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSaveFreq}
            disabled={savingFreq}
            style={{ marginTop: 18, padding: '8px 18px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            {savingFreq ? 'Saving...' : 'Save Schedule'}
          </button>
          {settings?.next_rebalance_date && (
            <div style={{ marginTop: 18, fontSize: 12, color: '#888' }}>
              Next scheduled: <span style={{ color: '#2196F3' }}>{new Date(settings.next_rebalance_date).toLocaleDateString('en-IN')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
